<template>
  <div class="pt-2 h-100 d-flex" :key="projectId">
    <div class="initiatives-view flex-grow-1 mh-0 mw-0 d-flex flex-column" v-if="project">
      <div class="bold text-extra-large title">
        {{ project.title }}
        <icon-dropdown
          icon-class="ellipsis-horizontal-icon"
          @change="onProjectSettingsSelection"
          :options="[
            { label: 'Edit Project', value: 'edit' },
            { label: 'Delete Project', value: 'delete' }
          ]"
        />
      </div>
      <div class="project-initiatives flex-grow-1 d-flex overflow-hidden">
        <initiatives-view
          ref="view"
          :project-id="project.id"
          :team-id="project.teamId"
          :org-id="orgId"
          :query="query"
          :hide-save-as-list="true"
          :show-parent="false"
          :default-filters-list="['resolution', 'show']"
          :key="project.id"
          :auto-save-column-settings="true"
          @section-added="fetchProjectSections"
        />
      </div>
      <manage-project
        v-model="editProjectModalShown"
        :project-id="project.id"
        :org-id="project.organizationId"
        v-if="editProjectModalShown"
      />
    </div>
    <confirm-dialog
      title="Delete this Project?"
      ref="confirmModal"
      @ok="triggerDeleteProject"
      ok-title="Delete Project"
      cancel-title="No, I changed my mind"
      hide-header-close
    >
      Deleting this Project will delete all related Tasks and Sub-tasks. This action cannot be
      undone.
    </confirm-dialog>
  </div>
</template>

<script>
import InitiativesView from '@/components/board/InitiativesView.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import IconDropdown from '@/components/IconDropdown.vue'
import ManageProject from '@/components/ManageProject.vue'
import events from '@/constants/events'
import { featureFlags } from '@/constants/featureFlags'
import { goalsTabIds } from '@/constants/goals'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import SubOrgIdTrackerMixin from '@/mixins/SubOrgIdTrackerMixin'
import TeamMixin from '@/mixins/TeamMixin'
import getDocumentTitle from '@/utils/getDocumentTitle'
import { getTeamPageRouteConfig } from '@/utils/navigationConfigs'
import showAPIError from '@/utils/showAPIError'
import showToast from '@/utils/showToast'
import * as R from 'ramda'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'Project',
  props: ['projectId', 'orgId', 'query'],
  mixins: [FeaturesMixin, EventBusMixin, TeamMixin, SubOrgIdTrackerMixin],
  components: { ConfirmDialog, InitiativesView, ManageProject, IconDropdown },
  data() {
    return {
      isFetching: true,
      sections: null,
      editProjectModalShown: false,
      isProjectsEnabled: !!this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_PROJECTS)
    }
  },
  computed: {
    ...mapGetters('project', ['projectById']),
    project() {
      return this.projectById(this.projectId)
    },
    teamId() {
      return this.project?.teamId
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    // This is required to set selected org. It is getting used in SubOrgIdTrackerMixin
    subOrgId() {
      return this.getClosestOrgLikeTeamId(this.teamId)
    }
  },
  methods: {
    ...mapActions('project', ['getProject', 'getTeamProjects', 'deleteProject', 'getSections']),
    async fetchProjectSections() {
      try {
        this.sections = await this.getSections({ projectId: this.projectId, orgId: this.orgId })
      } catch (e) {
        showAPIError(e)
      }
    },
    async fetchProject() {
      let project
      try {
        project = await this.getProject({
          projectId: this.projectId,
          orgId: this.orgId
        })
      } catch (e) {
        const status = R.path(['response', 'status'], e)
        if (status === 404) {
          this.eventBusEmit(events.SHOW_404)
          return
        }
      }
      this.getTeamProjects({
        teamId: project.teamId,
        orgId: this.orgId
      })
    },
    onProjectSettingsSelection({ value: selectionType }) {
      switch (selectionType) {
        case 'edit':
          this.editProjectModalShown = true
          break
        case 'delete':
          this.$refs.confirmModal.show()
          break
      }
    },
    async triggerDeleteProject() {
      try {
        const teamId = this.project.teamId
        await this.deleteProject({
          projectId: this.project.id,
          teamId,
          orgId: this.orgId
        })
        showToast('Project has deleted successfully')
        const location = getTeamPageRouteConfig({
          orgId: this.orgId,
          teamId,
          entity: goalsTabIds.goals
        })
        this.$router.replace(location)
      } catch (e) {
        showAPIError(e)
      }
    },
    updateDocumentTitle() {
      const title = this.project?.title
      const teamName = this.team?.name
      const org = this.getClosestOrgLikeTeam(this.team?.id)
      if (title && teamName) {
        document.title = getDocumentTitle({
          title,
          teamName,
          orgName: org.name
        })
      }
    }
  },
  async mounted() {
    if (!this.isProjectsEnabled) {
      Promise.resolve().then(() => this.eventBusEmit(events.SHOW_404))
    } else if (!this.project) {
      this.fetchProject()
    }
    this.updateDocumentTitle()
    this.fetchProjectSections() // this is required for showing sections' name
  },
  watch: {
    projectId() {
      this.sections = null
      this.fetchProjectSections()
    },
    project: 'updateDocumentTitle'
  }
}
</script>

<style lang="scss" scoped>
$left-spacing: 40px;

.project-initiatives {
  padding: 16px 0 0 $left-spacing;
}

.title {
  padding-left: $left-spacing;
}

/deep/ .initiative-list-view {
  margin-left: -40px;
}
</style>
